import { INTEGER_REGEX, FLOAT_REGEX, CSV_TABLES_V1 } from 'business/constants';
import { DataTypes } from 'business/definitions';
import { getTableName } from 'helpers/helpers';
import { getOptionFunction } from 'routes/Explore/components/Sentence/helpers/treeData';
import { TableMetadata } from './types';

export const FILES_IMPORT = 'filesImport';

type ColumnT = {
    type: string;
    name: string;
    title?: string;
    scale?: TimeScalesT;
    key: string;
};

export type RawLine = { [header: string]: string };

export const getFileImportTablesV1 = (client: ClientT): string[] =>
    // @ts-expect-error [TS migration] Was not detected by flow
    client.bind in CSV_TABLES_V1 ? CSV_TABLES_V1[client.bind] : [];

// If column doesn't have a column type yet (new file),
// try to guess it by looking at the first row's data
// TODO: test once this v0 is made for real
// This function is overloaded to handle legacy behavior. Before the TS migration
// we were wrongfully passing a string instead of ColumnT sometimes.
// This is temporary and should be properly fixed.
export function getColumnType(
    column: string,
    rows: RawLine[],
): typeof DataTypes.text;
export function getColumnType(column: ColumnT, rows: RawLine[]): string;
export function getColumnType(
    column: ColumnT | string,
    rows: RawLine[],
): string {
    let columnName: string;
    if (column instanceof Object) {
        if (column.type) return column.type;
        columnName = column.name;
    } else {
        // There is a bug here. We were wrongfully passing arguments of type string
        // to this function and it always returned 'text'. This is a temporary
        // fix to avoid breaking the legacy behavior.
        return DataTypes.text;
    }

    const row = rows.find((item) => item[columnName]);
    if (!row) return DataTypes.text;

    const cell = row[columnName];

    if (cell.match(INTEGER_REGEX)) return DataTypes.integer;

    if (cell.match(FLOAT_REGEX)) return DataTypes.float;

    try {
        const timestamp = Date.parse(cell);
        if (isNaN(timestamp)) return DataTypes.text;

        return DataTypes.date;
    } catch {
        return DataTypes.text;
    }
}

type TablesByFunction = { [func: string]: string[] };

export const groupTableByFunction = (
    availableMetrics: AvailableMetricT[] | undefined,
    autoGeneratedTables?: TableMetadata[],
): TablesByFunction => {
    if (!availableMetrics) {
        return {};
    }
    // TODO: remove this
    // it exists to remove the views used for the budget/datalake API
    // it needs to be removed when the backend will be able to hide tables in the graph
    const viewWithColumns = new Set(
        availableMetrics
            .filter(
                (metric) =>
                    metric.type !== 'table' &&
                    metric.functions.includes('VIEW'),
            )
            .map((metric) => getTableName(metric.field)),
    );
    const archivedCSVTables =
        autoGeneratedTables !== undefined
            ? autoGeneratedTables.filter((t) => t.isArchived).map((t) => t.name)
            : [];
    return availableMetrics
        .filter(
            (metric) =>
                metric.type === 'table' &&
                (!metric.functions.includes('VIEW') ||
                    viewWithColumns.has(metric.field)),
        )
        .reduce((tablesByFunction, metric) => {
            if (metric.functions) {
                const func = getOptionFunction(metric.functions);
                if (!tablesByFunction[func]) {
                    // eslint-disable-next-line no-param-reassign
                    tablesByFunction = { ...tablesByFunction, [func]: [] };
                }
                if (!archivedCSVTables.includes(metric.field))
                    tablesByFunction[func].push(metric.field);
            }
            return tablesByFunction;
        }, {} as TablesByFunction);
};
